import { STORAGE_USER_SETTINGS } from '@waitroom/models';
import { createContext, ReactElement, ReactNode, useContext } from 'react';
import { useLocalStorage, UseLocalStorageResponse } from '../../../../hooks/useLocalStorage';

// TODO: migrate to zustand and local storage

export interface UserSettings {
  /**
   * 0 - default (all)
   * 1 - all
   * 2 - only important
   * 3 - none
   */
  audioAlerts: 0 | 1 | 2 | 3;
  notifications: {
    requestAccess: boolean;
    queue?: boolean;
    sessionStart?: boolean;
  };
  textToSpeech: {
    voice?: string;
    rate?: number;
  };
}
export interface UserSettingsProviderProps {
  children: ReactNode;
}
export type UseUserSettingsResponse = UseLocalStorageResponse<UserSettings> & {
  deepMerge: <K extends keyof UserSettings>(key: K, newData: Partial<UserSettings[K]>) => void;
};

export const defaultSettings: UserSettings = {
  audioAlerts: 2,
  textToSpeech: {},
  notifications: {
    requestAccess: true,
  },
};

export const UseSettingsContext = createContext<UseUserSettingsResponse | undefined>(undefined);
export const useUserSettings = (): UseUserSettingsResponse => {
  const ctx = useContext(UseSettingsContext) as UseUserSettingsResponse;
  // ! temporary fix for boolean audio alerts
  ctx.state.audioAlerts = typeof ctx.state.audioAlerts === 'boolean' ? 0 : ctx.state.audioAlerts;
  return ctx;
};

export const UserSettingsProvider = ({
  children,
}: UserSettingsProviderProps): ReactElement | null => {
  const value = useLocalStorage<UserSettings>({
    key: STORAGE_USER_SETTINGS,
    defaultValue: defaultSettings,
    parse: true,
  });
  return (
    <UseSettingsContext.Provider
      value={{
        ...value,
        deepMerge: (key, newData) =>
          value.merge((prev) => {
            const prevK = prev?.[key];
            return {
              ...prev,
              [key]:
                typeof prevK === 'object'
                  ? {
                      ...prevK,
                      ...newData,
                    }
                  : newData,
            };
          }),
      }}
    >
      {children}
    </UseSettingsContext.Provider>
  );
};
